import React, { useCallback, useMemo, useState } from "react";
import {
  LanguageNameSpaces,
  SortingOrder,
  TradeDataWithKey,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import SortSelect from "common/Select/SortSelect";
import {
  sortTradesCards,
  TradesCardsSortKey,
} from "features/trades/common/tradesCardsSort.ts";

const SORT_VALUES = Object.values(TradesCardsSortKey);

const useNewLookTradesCardsSort = (isOpenTrades: boolean) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const [sortKey, setSortKey] = useState(TradesCardsSortKey.TIME);
  const [sortDirection, setSortDirection] = useState(SortingOrder.DESCEND);

  const labelMapper = useCallback(
    (value: TradesCardsSortKey) => t(getSortLabelKey(value, isOpenTrades)),
    [isOpenTrades, t]
  );

  const sortTrades = useCallback(
    (trades: TradeDataWithKey[]) =>
      sortTradesCards(trades, sortKey, sortDirection, isOpenTrades),
    [sortKey, sortDirection, isOpenTrades]
  );

  const sortControl = useMemo(
    () => (
      <SortSelect
        sortBy={sortKey}
        setSortBy={setSortKey}
        sortDirection={sortDirection}
        setSortDirection={setSortDirection}
        sortValues={SORT_VALUES}
        labelMapper={labelMapper}
      />
    ),
    [sortKey, sortDirection, labelMapper]
  );

  return { sortTrades, sortControl };
};

export default useNewLookTradesCardsSort;

const getSortLabelKey = (value: TradesCardsSortKey, isOpenTrades: boolean) => {
  if (value === TradesCardsSortKey.SYMBOL) return "table.headers.symbol";
  if (value === TradesCardsSortKey.AMOUNT) return "table.headers.amount";
  if (value === TradesCardsSortKey.PNL) return "table.headers.pnl";
  return isOpenTrades ? "table.headers.opened_at" : "table.headers.closed_at";
};
